import type { StateCreator } from 'zustand'
import { createStore } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import { IS_PROD_LIKE_ENVIRONMENT } from '@/constants/env'

import type { UUID } from '@/types/Id'

export type SourceViewerState = 'hidden' | 'collapsed' | 'open'
export type WikiPanelTab = 'files' | 'citations' | 'discussion'

export interface WikiCitationStoreState {
  tocOpen: boolean
  setTocOpen: (open: boolean) => void

  sourceViewerState: SourceViewerState
  setSourceViewerState: (state: SourceViewerState) => void

  activeSourceIds: number[]
  setActiveSourceIds: (ids: number[]) => void

  activeTab: WikiPanelTab | undefined
  setActiveTab: (tab: WikiPanelTab) => void

  viewingFileId: UUID | null
  setViewingFileId: (id: UUID | null) => void

  openSourceViewer: (activeTab?: WikiPanelTab) => void
  closeSourceViewer: () => void
  toggleDiscussion: () => void
}

const makeInitStore: StateCreator<WikiCitationStoreState> = (set) => ({
  tocOpen: true,
  setTocOpen: (open) => set({ tocOpen: open }),

  sourceViewerState: 'hidden',
  setSourceViewerState: (state) => set({ sourceViewerState: state }),

  activeSourceIds: [],
  setActiveSourceIds: (ids) => set({ activeSourceIds: ids }),

  activeTab: 'files',
  setActiveTab: (tab) => set({ activeTab: tab }),

  viewingFileId: null,
  setViewingFileId: (id) => set({ viewingFileId: id }),

  openSourceViewer: (activeTab: WikiPanelTab = 'citations') =>
    set({
      activeTab,
      sourceViewerState: 'open',
      tocOpen: false,
    }),

  closeSourceViewer: () =>
    set({
      sourceViewerState: 'hidden',
      tocOpen: true,
      activeSourceIds: [],
      viewingFileId: null,
      activeTab: undefined,
    }),

  toggleDiscussion: () =>
    set((state) =>
      state.activeTab === 'discussion' && state.sourceViewerState !== 'hidden'
        ? {
            sourceViewerState: 'hidden',
            tocOpen: true,
            activeSourceIds: [],
            viewingFileId: null,
            activeTab: undefined,
          }
        : {
            sourceViewerState: 'open',
            tocOpen: false,
            activeTab: 'discussion',
            activeSourceIds: [],
            viewingFileId: null,
          },
    ),
})

export type WikiCitationStore = ReturnType<typeof createWikiCitationStore>

export const createWikiCitationStore = () => {
  if (IS_PROD_LIKE_ENVIRONMENT) {
    return createStore<WikiCitationStoreState, [['zustand/immer', never]]>(
      immer<WikiCitationStoreState>(makeInitStore),
    )
  }

  return createStore<
    WikiCitationStoreState,
    [['zustand/devtools', never], ['zustand/immer', never]]
  >(devtools(immer<WikiCitationStoreState>(makeInitStore)))
}
